import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import GET_PAGES_WITH_OLD_URLS from "apollo/queries/GET_PAGES_WITH_OLD_URLS"
import { getNonUmbracoUrl } from "./getNonUmbracoUrl"

interface Link {
  url: string
  [key: string]: any
}

interface PropertyValue {
  links: Link[]
  [key: string]: any
}

interface Property {
  propertyName: string
  propertyValue: PropertyValue
  [key: string]: any
}

const GRAPHQL_URL = process.env.GRAPHQL_URL
if (!GRAPHQL_URL) {
  throw new Error("Please provide the GRAPHQL_URL environment variable.")
}

// Custom fetch function to log only errors
const customFetch = async (
  uri: string,
  options: RequestInit & { headers?: { "x-forwarded-for"?: string } },
) => {
  const res = await fetch(uri, options)

  // Only log if there's an error
  if (!res.ok) {
    const requestBody = options?.body ? JSON.parse(options.body as string) : null
    const ip = options?.headers?.["x-forwarded-for"] || "Unknown IP"

    console.error("=== HTTP Request Failed ===")
    console.error(`URL: ${uri}`)
    console.error(`Operation: ${requestBody?.operationName || "Unknown"}`)
    console.error(`IP: ${ip}`)
    console.error(`Status: ${res.status} ${res.statusText}`)

    try {
      const responseText = await res.clone().text()
      if (responseText) {
        console.error(`Response Body: ${responseText}`)
      }
    } catch (e) {
      console.error("Could not read response body")
    }
    console.error("===========================")
  } else {
    // Check for GraphQL errors in successful HTTP responses
    try {
      const responseClone = res.clone()
      const responseData = await responseClone.json()

      if (responseData.errors) {
        const requestBody = options?.body ? JSON.parse(options.body as string) : null
        console.error("🚨 GraphQL Errors in Response:")
        console.error(`Operation: ${requestBody?.operationName || "Unknown"}`)
        console.error(`Variables: ${JSON.stringify(requestBody?.variables || {}, null, 2)}`)
        console.error("Errors:", responseData.errors)
      }
    } catch (e) {
      // Only log JSON parsing errors if they might be significant
      console.error("❌ Could not parse response as JSON:", e instanceof Error ? e.message : e)
    }
  }

  return res
}

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // Extract route information from operation variables
  const getRouteInfo = () => {
    const variables = operation.variables
    if (variables) {
      // Check for different types of route/path variables
      const route = variables.route || variables.path || variables.url
      const header = variables.header
      const desktopFooter = variables.desktopFooter
      const mobileFooter = variables.mobileFooter
      const culture = variables.culture || variables.locale || variables.lang

      let routeInfo = ""
      if (route) routeInfo += `Route: ${route}`
      if (header) routeInfo += routeInfo ? `, Header: ${header}` : `Header: ${header}`
      if (desktopFooter)
        routeInfo += routeInfo
          ? `, Desktop Footer: ${desktopFooter}`
          : `Desktop Footer: ${desktopFooter}`
      if (mobileFooter)
        routeInfo += routeInfo
          ? `, Mobile Footer: ${mobileFooter}`
          : `Mobile Footer: ${mobileFooter}`
      if (culture) routeInfo += routeInfo ? `, Culture: ${culture}` : `Culture: ${culture}`

      return routeInfo || "No route info available"
    }
    return "No variables available"
  }

  // Handle GraphQL errors
  if (graphQLErrors) {
    const ip = operation.getContext()?.ip || "Unknown IP"
    const routeInfo = getRouteInfo()

    console.error("=== GraphQL Errors ===")
    console.error(`Operation: ${operation.operationName || "Unknown"}`)
    console.error(`${routeInfo}`)
    console.error(`IP: ${ip}`)

    graphQLErrors.forEach((error, index) => {
      console.error(`--- Error ${index + 1} ---`)
      console.error(`Message: ${error.message}`)

      if (error.locations) {
        console.error(
          `Locations:`,
          error.locations.map((loc) => `Line ${loc.line}, Column ${loc.column}`),
        )
      }

      if (error.path) {
        console.error(`Path: ${error.path.join(" -> ")}`)
      }

      if (error.extensions) {
        console.error(`Extensions:`, JSON.stringify(error.extensions, null, 2))
      }

      console.error("---")
    })
    console.error("======================")
  }

  // Handle network errors
  if (networkError) {
    const ip = operation.getContext()?.ip || "Unknown IP"
    const routeInfo = getRouteInfo()

    console.error("=== Network Error ===")
    console.error(`Operation: ${operation.operationName || "Unknown"}`)
    console.error(`${routeInfo}`)
    console.error(`IP: ${ip}`)
    console.error(`Error: ${networkError.message}`)

    if ("statusCode" in networkError) {
      console.error(`Status Code: ${networkError.statusCode}`)
    }

    if ("result" in networkError && networkError.result) {
      console.error(`Server Response:`, JSON.stringify(networkError.result, null, 2))
    }
    console.error("=====================")
  }
})

export const getClientServerSide = new ApolloClient({
  link: ApolloLink.from([
    errorLink,
    new HttpLink({
      uri: GRAPHQL_URL,
      fetch: customFetch, // Use the custom fetch function
      headers: {
        "x-forwarded-for": "IP not available", // Placeholder IP for client-side fetches
      },
    }),
  ]),
  cache: new InMemoryCache(),
})

export type Redirect = {
  source: string
  destination: string
}

declare global {
  interface Global {
    redirectsCache: {
      lastFetch: number
      data: Redirect[] | null
    }
  }
}

;(global as Global & typeof globalThis).redirectsCache = (global as Global & typeof globalThis)
  .redirectsCache || {
  lastFetch: 0,
  data: null,
}

export async function getCachedRedirects(pathBase: string): Promise<Redirect[] | null> {
  const oneDayInMillis = 60 * 60 * 1000 // 1 hours in milliseconds
  const now = Date.now()
  const globalObject = global as Global & typeof globalThis
  console.log(pathBase)
  // Check if the cache is older than a day or empty
  if (
    !globalObject.redirectsCache.data ||
    now - globalObject.redirectsCache.lastFetch > oneDayInMillis
  ) {
    try {
      // Fetch data for Hebrew culture with error policy to continue despite GraphQL errors
      const { data: heData } = await getClientServerSide.query({
        query: GET_PAGES_WITH_OLD_URLS,
        variables: {
          route: `${pathBase}`,
          culture: "he-il",
        },
        errorPolicy: "all", // Continue even if there are GraphQL errors
      })

      // Fetch data for English culture with error policy to continue despite GraphQL errors
      const { data: enData } = await getClientServerSide.query({
        query: GET_PAGES_WITH_OLD_URLS,
        variables: {
          route: `${pathBase}`,
          culture: "en-us",
        },
        errorPolicy: "all", // Continue even if there are GraphQL errors
      })
      console.log("FETCH REDIRECTS")
      const extractOldUrls = (nodes: any, isEnglish = false) => {
        const urls: Redirect[] = []
        nodes.forEach((node: any) => {
          // Add safety check for node properties
          if (!node.properties || !Array.isArray(node.properties)) {
            return
          }

          node.properties.forEach((prop: any) => {
            if (prop.propertyName === "oldUrls" && prop.propertyValue?.links) {
              const updatedLinks: Link[] = prop.propertyValue.links.map((link: Link): Link => {
                if (link.url.startsWith("http://") || link.url.startsWith("https://")) {
                  const url = new URL(link.url, GRAPHQL_URL)
                  if (url.origin !== GRAPHQL_URL) {
                    return {
                      ...link,
                      url: url.pathname,
                    }
                  }
                }
                return link
              })
              updatedLinks.forEach((link: any) => {
                const source = link.url
                  .replace(/\/$/, "")
                  .replace(/\/he-IL$/, "")
                  .replace(/\/en-US$/, "")
                const destination = getNonUmbracoUrl(
                  node.url
                    .replace(/\/$/, "")
                    .replace(/\/he-IL$/, "")
                    .replace(/\/en-US$/, ""),
                )

                const redirect: Redirect = {
                  destination,
                  source: source,
                }

                urls.push(redirect)
              })
            }
          })

          // Recursively extract from children if they exist
          if (node.children && node.children.length > 0) {
            urls.push(...extractOldUrls(node.children, isEnglish))
          }
        })
        return urls
      }

      // Extract URLs from both Hebrew and English responses
      const heUrls = heData?.components?.edges
        ? extractOldUrls(heData.components.edges.map((edge: any) => edge.node))
        : []
      const enUrls = enData?.components?.edges
        ? extractOldUrls(
            enData.components.edges.map((edge: any) => edge.node),
            true,
          )
        : []

      console.log(enUrls)
      // Combine both arrays
      const oldUrls = [...enUrls, ...heUrls]
      // console.log({ oldUrls })

      globalObject.redirectsCache = {
        lastFetch: now,
        data: oldUrls,
      }
    } catch (error) {
      console.error("Error fetching redirects cache:", error)
      // Return cached data if available, even if stale
      if (globalObject.redirectsCache.data) {
        console.log("Returning stale cache data due to error")
        return globalObject.redirectsCache.data
      }
      // If no cached data available, return empty array to prevent app crash
      return []
    }
  }

  return globalObject.redirectsCache.data
}

export {}

// Function to replace dynamic path placeholders, or return direct destination for plain URLs
export const replacePath = (destination: string, params?: Record<string, string>): string => {
  if (!params) {
    return destination
  } // Return the direct destination if there are no parameters to replace
  return destination.replace(/:\w+\*/g, (match) => {
    const key = match.slice(1, -1) // Remove the colon and asterisk
    return params[key] || "" // Replace with the parameter value or empty string
  })
}

// Extract dynamic segments from the path or return {} for a direct match
export const extractParams = (pattern: string, path: string): Record<string, string> | null => {
  // Normalize the paths for comparison
  const source = decodeURI(pattern.toLowerCase().replace(/^\/|\/$/g, ""))
  const current = decodeURI(path.toLowerCase().replace(/^\/|\/$/g, ""))
  if (!source.includes(":")) {
    const match = source === current
    return match ? {} : null // Direct match for plain URLs
  }
  const regex = new RegExp("^" + source.replace(/:\w+\*/g, "(.*)") + "$", "i") // 'i' for case-insensitive regex
  const match = current.match(regex)

  return match ? { path: match[1] } : null // Assuming only one dynamic segment for simplicity
}
